import { auth, type StateAuth } from "./modules/auth";
import { catalogs, type StateCatalogs } from "./modules/catalogs";
import { type StateTranslation, translations } from "./modules/translations";
import { cart, defaultCartInit, type StateCart } from "./modules/cart";
import { transaction, type StateTransaction } from "./modules/transaction";
import { session } from "./modules/session";
import {
  assembly,
  defaultAssemblyInit,
  type StateAssembly,
} from "./modules/assembly";
import {
  checkout,
  defaultCheckoutInit,
  type StateCheckout,
} from "./modules/checkout";
import {
  checkoutTransaction,
  type StateCheckoutTransaction,
  defaultCheckoutTransactionInit,
} from "./modules/checkout-transaction";
import { cfdi } from "./modules/cfdi";
import wishlist, { type WishlistState } from "./modules/wishlist";

import { createStore, createLogger, useStore as baseUseStore } from "vuex";

import type { Store } from "vuex/types/index.js";

import type { InjectionKey } from "vue";

export interface RootState {
  canCloseMenu: boolean;
  theme: string | null;
  pageTransition: {
    name: string;
    mode: string;
  };
  catalogs?: StateCatalogs;
  translations?: StateTranslation;
  checkout?: StateCheckout;
  checkoutTransaction?: StateCheckoutTransaction;
  cart?: StateCart;
  transaction?: StateTransaction;
  wishlist?: WishlistState;
  auth?: StateAuth;
  assembly?: StateAssembly;
}

const PERSISTED_STATES = [
  "canCloseMenu",
  "theme",
  "pageTransition",
  "cart",
  "transaction",
  "checkout",
  "checkoutTransaction",
  "auth",
  "assembly",
];

const debug = false; //TODO: Cambié a false mientras tanto que no se trabaja con esto para no ver todos los mensajes que envía process.env.NODE_ENV !== 'production' Cambai

const persistState = (store: Store<RootState>) => {
  const savedStateRaw =
    typeof localStorage === "object" ? localStorage.getItem("vuex") : null;
  let savedState: RootState | undefined;
  try {
    savedState = (savedStateRaw ? JSON.parse(savedStateRaw) : {}) as RootState;
  } catch (error) {
    console.error("Failed to parse saved state: ", error);
  }
  if (savedState) {
    savedState.catalogs = {
      categories: {
        links: ["hi"],
        data: [],
        included: [],
        meta: [],
      },
      brands: [],
      banners: {
        links: [],
        data: [],
        included: [],
        meta: [],
      },
    };
    savedState.translations = {
      sections: [],
      loading: [],
    };
  }
  if (
    savedState &&
    savedState.checkout?.version !== defaultCheckoutInit?.version
  ) {
    savedState.checkout = defaultCheckoutInit;
  }
  if (
    savedState &&
    savedState.checkoutTransaction?.version !==
      defaultCheckoutTransactionInit?.version
  ) {
    savedState.checkoutTransaction = defaultCheckoutTransactionInit;
  }
  if (
    savedState &&
    (savedState.cart?.version !== defaultCartInit?.version ||
      !savedState.cart?.version)
  ) {
    savedState.cart = defaultCartInit;
  }
  if (
    savedState &&
    (savedState.assembly?.version !== defaultAssemblyInit?.version ||
      !savedState.assembly?.version)
  ) {
    savedState.assembly = defaultAssemblyInit;
  }
  // Inyecta el state del storage filtrado según el aceptado
  if (savedState) {
    const savedFiltered = filterPersistedState(savedState);
    store.replaceState({ ...store.state, ...savedFiltered });
  }

  let debounceTimeout: number;
  // Guarda los datos de Vuex cada cierto tiempo para no sobrecargar.
  store.subscribe((_mutation, state) => {
    clearTimeout(debounceTimeout);
    if (typeof window === "object") {
      debounceTimeout = window.setTimeout(() => {
        const persisted = filterPersistedState(state);
        localStorage.setItem("vuex", JSON.stringify(persisted));
      }, 500);
    }
  });
};

/**
 * Filtra el state según los permitidos (PERSISTED_STATES) para estar persisitentes.
 * Note: Deberíamos mejorar este método porque si en Vuex se guarda un objeto corrupto podría afactar la funcionalidad, ej. Pensar en qué pasaría si Local Sate: {a: 1} reemplaza al State Actual: {a: {b: 1, c: 2}}
 */
const filterPersistedState = (state: RootState): RootState => {
  const persisted: Record<string, unknown> = {};
  PERSISTED_STATES.forEach((key) => {
    const stateCasted = state as unknown as Record<string, unknown>;
    if (stateCasted[key] !== undefined) {
      persisted[key] = stateCasted[key];
    }
  });
  return persisted as unknown as RootState;
};

const rootState = (): RootState => ({
  theme: "light",
  canCloseMenu: false,
  pageTransition: {
    name: "router-view",
    mode: "in-out",
  },
});

const rootGetters = {
  getTheme(state: RootState) {
    return state.theme;
  },
  getCanCloseMenu(state: RootState) {
    return state.canCloseMenu;
  },
  getImageNotAvailable() {
    return "../images/imagen-no-disponible.png";
  },
};

const rootMutations = {
  setPageTransition(state: RootState, value: string) {
    if ("default" === value) {
      state.pageTransition = {
        name: "router-view",
        mode: "in-out",
      };
    }
    if ("back" === value) {
      state.pageTransition = {
        name: "router-view-back",
        mode: "",
      };
    }
  },
  toggleTheme(state: RootState) {
    state.theme = state.theme == "light" ? "dark" : "light";
  },
  setCanCloseMenu(state: RootState, canCloseMenu: boolean) {
    state.canCloseMenu = canCloseMenu;
  },
};

export const store = createStore<RootState>({
  modules: {
    auth,
    catalogs,
    translations,
    cart,
    transaction,
    session,
    assembly,
    checkout,
    checkoutTransaction,
    cfdi,
    wishlist,
  },

  state: rootState,
  getters: rootGetters,
  mutations: rootMutations,

  strict: debug,
  plugins: debug ? [createLogger(), persistState] : [persistState],
});

export const key: InjectionKey<Store<RootState>> = Symbol();

export function useStoreWithKey() {
  return baseUseStore(key);
}
